import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type FormEvent,
  type ReactNode,
} from 'react';
import type { ManagedOrganizationMember } from '@wx-weiqi/contracts';

import { memberLoginActionLabel } from '../domain/account-access-presentation';
import type { AdminStore } from '../stores/admin-store';
import './admin-panel.css';

type View = 'ORGANIZATIONS' | 'USERS';
type Editor = 'ORGANIZATION' | 'NEW_ORGANIZATION' | 'ACCOUNT';

function AdminIcon({ kind }: { readonly kind: 'organization' | 'users' | 'plus' | 'refresh' }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {kind === 'organization' ? (
        <>
          <path d="M4 21V5l8-3 8 3v16M2 21h20M9 21v-5h6v5M8 7h1m6 0h1M8 11h1m6 0h1" />
        </>
      ) : null}
      {kind === 'users' ? (
        <>
          <circle cx="9" cy="8" r="3" />
          <path d="M3 21v-2a6 6 0 0 1 12 0v2M16 5a3 3 0 0 1 0 6m2 4a5 5 0 0 1 3 5" />
        </>
      ) : null}
      {kind === 'plus' ? <path d="M12 5v14M5 12h14" /> : null}
      {kind === 'refresh' ? (
        <path d="M20 7v5h-5M4 17v-5h5M5.5 7a7.5 7.5 0 0 1 12.3-1L20 9M4 15l2.2 3A7.5 7.5 0 0 0 18.5 17" />
      ) : null}
    </svg>
  );
}

export function AdminPanel({
  admin,
  isPlatformAdmin,
}: {
  readonly admin: AdminStore;
  readonly isPlatformAdmin: boolean;
}) {
  const state = useSyncExternalStore(admin.subscribe, admin.snapshot);
  const [view, setView] = useState<View>('ORGANIZATIONS');
  const [editor, setEditor] = useState<Editor | null>(null);
  const [notice, setNotice] = useState('');
  const [organizationSearch, setOrganizationSearch] = useState('');
  const [appliedOrganizationSearch, setAppliedOrganizationSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userRole, setUserRole] = useState<'' | 'STUDENT' | 'TEACHER' | 'ADMIN'>('');
  const [userStatus, setUserStatus] = useState<'' | 'ACTIVE' | 'INACTIVE'>('');
  const [organizationName, setOrganizationName] = useState('');
  const [newOrganizationName, setNewOrganizationName] = useState('');
  const [organizationSlug, setOrganizationSlug] = useState('');
  const [accountUsername, setAccountUsername] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [accountDisplayName, setAccountDisplayName] = useState('');
  const [accountRole, setAccountRole] = useState<'STUDENT' | 'TEACHER'>('STUDENT');
  const busy = state.pending !== null;
  const swallow = (operation: Promise<void>) => void operation.catch(() => undefined);
  const selected = state.organizations.find(
    (organization) => organization.organizationId === state.selectedOrganizationId,
  );

  useEffect(() => {
    void admin.load().catch(() => undefined);
  }, [admin]);
  useEffect(() => {
    setOrganizationName(state.organization?.name ?? '');
  }, [state.organization]);
  useEffect(() => {
    setUserSearch(state.memberFilters.search ?? '');
    setUserRole(state.memberFilters.role ?? '');
    setUserStatus(state.memberFilters.status ?? '');
  }, [state.memberFilters]);

  const navigate = (next: View) => {
    setView(next);
    setNotice('');
    if (next === 'USERS' && appliedOrganizationSearch !== '') {
      setOrganizationSearch('');
      setAppliedOrganizationSearch('');
      swallow(admin.loadPlatformOrganizations());
    }
  };
  const selectOrganization = async (organizationId: string, next: 'DETAIL' | 'USERS') => {
    await admin.selectOrganization(organizationId);
    if (next === 'DETAIL') setEditor('ORGANIZATION');
    else navigate('USERS');
  };
  const refresh = () =>
    swallow(
      view === 'ORGANIZATIONS'
        ? isPlatformAdmin
          ? admin.loadPlatformOrganizations(appliedOrganizationSearch)
          : admin.load()
        : admin.loadMembers(state.memberFilters),
    );
  const filterUsers = (event: FormEvent) => {
    event.preventDefault();
    swallow(
      admin.loadMembers({
        ...(userSearch.trim() === '' ? {} : { search: userSearch }),
        ...(userRole === '' ? {} : { role: userRole }),
        ...(userStatus === '' ? {} : { status: userStatus }),
      }),
    );
  };
  const save = (operation: Promise<void>, message: string) =>
    swallow(
      operation.then(() => {
        setEditor(null);
        setNotice(message);
      }),
    );
  const createOrganization = (event: FormEvent) => {
    event.preventDefault();
    save(
      admin.createOrganization(newOrganizationName, organizationSlug).then(() => {
        setNewOrganizationName('');
        setOrganizationSlug('');
        setOrganizationSearch('');
        setAppliedOrganizationSearch('');
      }),
      '棋院已添加。',
    );
  };
  const createAccount = (event: FormEvent) => {
    event.preventDefault();
    save(
      admin
        .createAccount({
          username: accountUsername,
          password: accountPassword,
          displayName: accountDisplayName,
          role: accountRole,
        })
        .then(() => {
          setAccountUsername('');
          setAccountPassword('');
          setAccountDisplayName('');
        }),
      '账号已添加到当前棋院。',
    );
  };
  const renameOrganization = (event: FormEvent) => {
    event.preventDefault();
    save(
      admin.renameOrganization(organizationName).then(async () => {
        if (isPlatformAdmin) await admin.loadPlatformOrganizations(appliedOrganizationSearch);
      }),
      '棋院信息已保存。',
    );
  };

  return (
    <main className="admin-console">
      <aside className="admin-console__sidebar">
        <div className="admin-console__identity">
          <span className="admin-console__emblem">
            <AdminIcon kind="organization" />
          </span>
          <div>
            <strong>{isPlatformAdmin ? '平台管理' : '棋院管理'}</strong>
            <small>弈时围棋 · 管理工作台</small>
          </div>
        </div>
        <p className="admin-console__nav-label">管理功能</p>
        <nav aria-label="管理功能">
          <button
            type="button"
            aria-current={view === 'ORGANIZATIONS' ? 'page' : undefined}
            onClick={() => navigate('ORGANIZATIONS')}
          >
            <AdminIcon kind="organization" />
            棋院管理<span aria-hidden="true">›</span>
          </button>
          <button
            type="button"
            aria-current={view === 'USERS' ? 'page' : undefined}
            onClick={() => navigate('USERS')}
          >
            <AdminIcon kind="users" />
            用户管理<span aria-hidden="true">›</span>
          </button>
        </nav>
        <div className="admin-console__scope">
          <small>管理范围</small>
          <strong>
            {isPlatformAdmin ? '平台全部棋院' : (state.organization?.name ?? '当前棋院')}
          </strong>
          <span>{isPlatformAdmin ? '平台管理员' : '机构管理员'}</span>
        </div>
      </aside>
      <div className="admin-console__workspace">
        <header className="admin-console__heading">
          <div>
            <p className="admin-console__breadcrumb">
              管理工作台 <span>/</span> {view === 'ORGANIZATIONS' ? '棋院管理' : '用户管理'}
            </p>
            <h1>{view === 'ORGANIZATIONS' ? '棋院管理' : '用户管理'}</h1>
            <p>
              {view === 'ORGANIZATIONS'
                ? '管理棋院资料，查看各棋院的关联用户。'
                : '按棋院查看用户，管理账号角色与登录状态。'}
            </p>
          </div>
          <div className="admin-console__heading-actions">
            <button className="ac-button" type="button" onClick={refresh} disabled={busy}>
              <AdminIcon kind="refresh" />
              刷新
            </button>
            {view === 'USERS' || isPlatformAdmin ? (
              <button
                className="ac-button ac-button--primary"
                type="button"
                disabled={busy || (view === 'USERS' && state.organization === null)}
                onClick={() => {
                  setNotice('');
                  setEditor(view === 'ORGANIZATIONS' ? 'NEW_ORGANIZATION' : 'ACCOUNT');
                }}
              >
                <AdminIcon kind="plus" />
                {view === 'ORGANIZATIONS' ? '新增棋院' : '新增账号'}
              </button>
            ) : null}
          </div>
        </header>
        {state.error !== null && editor === null ? (
          <p className="ac-feedback ac-feedback--error" role="alert">
            {state.error}
          </p>
        ) : null}
        {notice !== '' ? (
          <p className="ac-feedback" role="status">
            {notice}
          </p>
        ) : null}
        {view === 'ORGANIZATIONS' ? (
          <section className="ac-panel" aria-label="棋院列表" aria-busy={busy}>
            <div className="ac-panel__heading">
              <h2>
                {isPlatformAdmin ? '全部棋院' : '我的棋院'}
                <span className="ac-count">
                  {isPlatformAdmin
                    ? state.organizations.length
                    : state.organization === null
                      ? 0
                      : 1}
                </span>
              </h2>
              <span className="ac-muted">
                {appliedOrganizationSearch ? '当前搜索结果' : '棋院资料与成员概况'}
              </span>
            </div>
            {isPlatformAdmin ? (
              <form
                className="ac-search"
                onSubmit={(event) => {
                  event.preventDefault();
                  setAppliedOrganizationSearch(organizationSearch.trim());
                  swallow(admin.loadPlatformOrganizations(organizationSearch));
                }}
              >
                <label>
                  <span className="ac-sr-only">搜索棋院</span>
                  <input
                    type="search"
                    value={organizationSearch}
                    onChange={(event) => setOrganizationSearch(event.target.value)}
                    placeholder="搜索棋院名称或唯一标识"
                  />
                </label>
                <button className="ac-button" type="submit" disabled={busy}>
                  查询
                </button>
                {appliedOrganizationSearch !== '' ? (
                  <button
                    className="ac-text-button"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setOrganizationSearch('');
                      setAppliedOrganizationSearch('');
                      swallow(admin.loadPlatformOrganizations());
                    }}
                  >
                    重置
                  </button>
                ) : null}
              </form>
            ) : null}
            {isPlatformAdmin ? (
              <div className="ac-table-wrap">
                <table className="ac-table ac-table--organizations">
                  <thead>
                    <tr>
                      <th>棋院名称</th>
                      <th>唯一标识</th>
                      <th>关联用户</th>
                      <th>正常成员</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.organizations.map((organization) => (
                      <tr key={organization.organizationId}>
                        <td data-label="棋院名称">
                          <div className="ac-table-identity">
                            <span className="ac-academy-icon">
                              <AdminIcon kind="organization" />
                            </span>
                            <strong>{organization.name}</strong>
                          </div>
                        </td>
                        <td data-label="唯一标识">
                          <span className="ac-slug">{organization.slug}</span>
                        </td>
                        <td data-label="关联用户">
                          <span className="ac-number">{organization.memberCount}</span>{' '}
                          <small>人</small>
                        </td>
                        <td data-label="正常成员">
                          <span className="ac-number">{organization.activeMemberCount}</span>{' '}
                          <small>人</small>
                        </td>
                        <td data-label="操作">
                          <div className="ac-row-actions">
                            <button
                              type="button"
                              className="ac-text-button"
                              disabled={busy}
                              aria-label={`查看${organization.name}的用户`}
                              onClick={() =>
                                swallow(selectOrganization(organization.organizationId, 'USERS'))
                              }
                            >
                              查看用户
                            </button>
                            <button
                              type="button"
                              className="ac-text-button ac-text-button--quiet"
                              disabled={busy}
                              aria-label={`编辑${organization.name}`}
                              onClick={() =>
                                swallow(selectOrganization(organization.organizationId, 'DETAIL'))
                              }
                            >
                              详情 / 编辑
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : state.organization !== null ? (
              <div className="ac-own-organization">
                <span className="ac-academy-icon">
                  <AdminIcon kind="organization" />
                </span>
                <div>
                  <h2>{state.organization.name}</h2>
                  <p>{state.organization.slug}</p>
                </div>
                <div className="ac-row-actions">
                  <button
                    className="ac-button"
                    type="button"
                    disabled={busy}
                    onClick={() => setEditor('ORGANIZATION')}
                  >
                    编辑资料
                  </button>
                  <button
                    className="ac-button ac-button--primary"
                    type="button"
                    onClick={() => navigate('USERS')}
                  >
                    查看用户
                  </button>
                </div>
              </div>
            ) : null}
            {(isPlatformAdmin ? state.organizations.length === 0 : state.organization === null) ? (
              <div className="ac-empty" role="status">
                <AdminIcon kind="organization" />
                <strong>{busy ? '正在加载棋院…' : '没有找到棋院'}</strong>
                <p>
                  {busy
                    ? '请稍候'
                    : appliedOrganizationSearch
                      ? '请调整搜索内容后重试。'
                      : '添加棋院后，即可管理它的资料与用户。'}
                </p>
              </div>
            ) : null}
            <footer className="ac-panel__footer">
              <span>
                {busy
                  ? '正在更新…'
                  : isPlatformAdmin
                    ? `共 ${state.organizations.length} 家棋院${appliedOrganizationSearch ? '符合搜索条件' : ''}`
                    : '当前棋院'}
              </span>
              <span>关联用户包含正常与已停用成员</span>
            </footer>
          </section>
        ) : (
          <section className="ac-panel" aria-label="用户列表" aria-busy={busy}>
            <div className="ac-panel__heading">
              <div>
                <h2>{state.organization?.name ?? '请选择棋院'}</h2>
                <p className="ac-muted">当前棋院的用户账号</p>
              </div>
              <span className="ac-count">已显示 {state.members.length} 人</span>
            </div>
            <form className="ac-filters" onSubmit={filterUsers}>
              {isPlatformAdmin ? (
                <label>
                  所属棋院
                  <select
                    aria-label="所属棋院"
                    value={state.selectedOrganizationId ?? ''}
                    disabled={busy}
                    onChange={(event) => swallow(admin.selectOrganization(event.target.value))}
                  >
                    {state.organizations.map((organization) => (
                      <option key={organization.organizationId} value={organization.organizationId}>
                        {organization.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <label className="ac-filters__search">
                搜索用户
                <input
                  type="search"
                  value={userSearch}
                  onChange={(event) => setUserSearch(event.target.value)}
                  placeholder="姓名或登录账号"
                />
              </label>
              <label>
                角色
                <select
                  aria-label="角色"
                  value={userRole}
                  onChange={(event) => setUserRole(event.target.value as typeof userRole)}
                >
                  <option value="">全部角色</option>
                  <option value="STUDENT">学生</option>
                  <option value="TEACHER">教师</option>
                  <option value="ADMIN">机构管理员</option>
                </select>
              </label>
              <label>
                状态
                <select
                  aria-label="状态"
                  value={userStatus}
                  onChange={(event) => setUserStatus(event.target.value as typeof userStatus)}
                >
                  <option value="">全部状态</option>
                  <option value="ACTIVE">正常</option>
                  <option value="INACTIVE">已停用</option>
                </select>
              </label>
              <div className="ac-filters__actions">
                <button
                  className="ac-button ac-button--primary"
                  type="submit"
                  disabled={busy || state.organization === null}
                >
                  筛选
                </button>
                <button
                  className="ac-text-button ac-text-button--quiet"
                  type="button"
                  disabled={busy || state.organization === null}
                  onClick={() => swallow(admin.loadMembers())}
                >
                  重置
                </button>
              </div>
            </form>
            <div className="ac-table-wrap">
              <table className="ac-table ac-table--users">
                <thead>
                  <tr>
                    <th>用户 / 账号</th>
                    <th>所属棋院</th>
                    <th>角色</th>
                    <th>状态</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {state.members.map((member) => (
                    <AdminMemberRow
                      key={member.membershipId}
                      member={member}
                      organizationName={state.organization?.name ?? '—'}
                      admin={admin}
                      busy={busy}
                      onNotice={setNotice}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {state.members.length === 0 ? (
              <div className="ac-empty" role="status">
                <AdminIcon kind="users" />
                <strong>{busy ? '正在加载用户…' : '没有符合条件的用户'}</strong>
                <p>{busy ? '请稍候' : '调整筛选条件，或为当前棋院新增账号。'}</p>
              </div>
            ) : null}
            <footer className="ac-panel__footer">
              <span>{busy ? '正在更新…' : `已显示 ${state.members.length} 个账号`}</span>
              {state.membersNextAfter !== null ? (
                <button
                  type="button"
                  className="ac-button"
                  disabled={busy}
                  onClick={() => swallow(admin.loadMoreMembers())}
                >
                  加载更多用户
                </button>
              ) : (
                <span>已显示全部结果</span>
              )}
            </footer>
            <p className="ac-permission-note">角色与登录状态变更立即生效；停用账号前需确认。</p>
          </section>
        )}
      </div>
      {editor !== null ? (
        <AdminEditor
          title={
            editor === 'NEW_ORGANIZATION'
              ? '新增棋院'
              : editor === 'ORGANIZATION'
                ? '棋院详情'
                : '新增账号'
          }
          busy={busy}
          onClose={() => setEditor(null)}
        >
          {state.error !== null ? (
            <p className="ac-feedback ac-feedback--error" role="alert">
              {state.error}
            </p>
          ) : null}
          {editor === 'NEW_ORGANIZATION' ? (
            <form onSubmit={createOrganization} className="ac-editor-form">
              <p className="ac-muted">填写棋院基础信息，创建后即可添加教师和学生账号。</p>
              <label>
                棋院名称
                <input
                  value={newOrganizationName}
                  onChange={(event) => setNewOrganizationName(event.target.value)}
                  placeholder="输入棋院名称"
                  required
                  maxLength={128}
                />
              </label>
              <label>
                唯一标识
                <input
                  value={organizationSlug}
                  onChange={(event) => setOrganizationSlug(event.target.value.toLowerCase())}
                  placeholder="例如 hangzhou-qiyuan"
                  pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                  minLength={3}
                  maxLength={64}
                  required
                />
                <small>3–64 位小写英文字母、数字或连字符。</small>
              </label>
              <div className="ac-editor-actions">
                <button
                  className="ac-button"
                  type="button"
                  disabled={busy}
                  onClick={() => setEditor(null)}
                >
                  取消
                </button>
                <button className="ac-button ac-button--primary" type="submit" disabled={busy}>
                  {busy ? '正在添加…' : '新增棋院'}
                </button>
              </div>
            </form>
          ) : null}
          {editor === 'ORGANIZATION' ? (
            <form onSubmit={renameOrganization} className="ac-editor-form">
              <dl className="ac-detail">
                <div>
                  <dt>唯一标识</dt>
                  <dd>{state.organization?.slug ?? '—'}</dd>
                </div>
                <div>
                  <dt>关联用户</dt>
                  <dd>
                    {selected?.memberCount ??
                      `${state.members.length}${state.membersNextAfter !== null ? '+' : ''}`}{' '}
                    人
                  </dd>
                </div>
                <div>
                  <dt>棋院 ID</dt>
                  <dd>{state.organization?.organizationId ?? '—'}</dd>
                </div>
              </dl>
              <label>
                棋院名称
                <input
                  value={organizationName}
                  onChange={(event) => setOrganizationName(event.target.value)}
                  required
                  maxLength={128}
                />
              </label>
              <div className="ac-editor-actions">
                <button
                  className="ac-text-button"
                  type="button"
                  disabled={busy || state.organization === null}
                  onClick={() => {
                    setEditor(null);
                    navigate('USERS');
                  }}
                >
                  查看关联用户 →
                </button>
                <button
                  className="ac-button ac-button--primary"
                  type="submit"
                  disabled={
                    busy ||
                    organizationName.trim() === '' ||
                    organizationName === state.organization?.name
                  }
                >
                  {busy ? '正在保存…' : '保存基础信息'}
                </button>
              </div>
            </form>
          ) : null}
          {editor === 'ACCOUNT' ? (
            <form onSubmit={createAccount} className="ac-editor-form">
              <p className="ac-editor-context">
                所属棋院<strong>{state.organization?.name ?? '—'}</strong>
              </p>
              <div className="ac-form-columns">
                <label>
                  登录账号
                  <input
                    value={accountUsername}
                    onChange={(event) => setAccountUsername(event.target.value)}
                    autoComplete="off"
                    minLength={3}
                    maxLength={32}
                    required
                  />
                </label>
                <label>
                  显示名称
                  <input
                    value={accountDisplayName}
                    onChange={(event) => setAccountDisplayName(event.target.value)}
                    maxLength={64}
                    required
                  />
                </label>
              </div>
              <label>
                初始密码
                <input
                  type="password"
                  value={accountPassword}
                  onChange={(event) => setAccountPassword(event.target.value)}
                  minLength={6}
                  maxLength={128}
                  autoComplete="new-password"
                  required
                />
              </label>
              <label>
                账号角色
                <select
                  aria-label="账号角色"
                  value={accountRole}
                  onChange={(event) => setAccountRole(event.target.value as 'STUDENT' | 'TEACHER')}
                >
                  <option value="STUDENT">学生</option>
                  <option value="TEACHER">教师</option>
                </select>
              </label>
              <div className="ac-editor-actions">
                <button
                  className="ac-button"
                  type="button"
                  disabled={busy}
                  onClick={() => setEditor(null)}
                >
                  取消
                </button>
                <button
                  className="ac-button ac-button--primary"
                  type="submit"
                  disabled={busy || state.organization === null}
                >
                  {busy ? '正在创建…' : '创建账号'}
                </button>
              </div>
            </form>
          ) : null}
        </AdminEditor>
      ) : null}
    </main>
  );
}

export function AdminMemberRow({
  member,
  organizationName,
  admin,
  busy,
  onNotice,
}: {
  readonly member: ManagedOrganizationMember;
  readonly organizationName: string;
  readonly admin: AdminStore;
  readonly busy: boolean;
  readonly onNotice: (message: string) => void;
}) {
  const isPlatform = member.user.isPlatformAdmin === true;
  const locked =
    member.loginLockedUntil != null && Date.parse(member.loginLockedUntil) > Date.now();
  const inactive = member.status !== 'ACTIVE' || member.user.status !== 'ACTIVE';
  const unlock = () => {
    if (
      !window.confirm(
        `确定解除“${member.user.displayName}”的密码错误锁定吗？此操作不修改密码，也不会恢复已停用的账号。`,
      )
    )
      return;
    void admin
      .unlockAccount(member)
      .then(() =>
        onNotice(
          `${member.user.displayName}：已解除密码错误锁定${inactive ? '，账号停用状态保持不变' : '，可使用原密码重新登录'}。`,
        ),
      )
      .catch(() => undefined);
  };
  const nextStatus = member.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
  const changeLoginStatus = () => {
    if (
      nextStatus === 'INACTIVE' &&
      !window.confirm(
        `确定停用“${member.user.displayName}”的登录权限吗？停用后该账号将无法进入平台。`,
      )
    )
      return;
    void admin
      .updateMember(member, { status: nextStatus })
      .then(() =>
        onNotice(
          `${member.user.displayName}：${nextStatus === 'ACTIVE' ? '已恢复登录' : '已停用登录'}。`,
        ),
      )
      .catch(() => undefined);
  };
  return (
    <tr>
      <td data-label="用户 / 账号">
        <div className="ac-table-identity">
          <span className={`ac-avatar ac-avatar--${member.role.toLowerCase()}`}>
            {member.user.displayName.slice(0, 1)}
          </span>
          <div>
            <strong>{member.user.displayName}</strong>
            <small>
              {member.accountUsername ?? '微信账号'}
              {member.user.status !== 'ACTIVE'
                ? ` · ${member.user.status === 'SUSPENDED' ? '用户已停权' : '用户已禁用'}`
                : ''}
            </small>
          </div>
        </div>
      </td>
      <td data-label="所属棋院">{organizationName}</td>
      <td data-label="角色">
        {isPlatform ? (
          <span className="ac-platform-role">平台管理员</span>
        ) : (
          <select
            aria-label={`${member.user.displayName}的角色`}
            value={member.role}
            disabled={busy}
            onChange={(event) =>
              void admin
                .updateMember(member, {
                  role: event.target.value as 'STUDENT' | 'TEACHER' | 'ADMIN',
                })
                .then(() => onNotice(`${member.user.displayName}的角色已更新。`))
                .catch(() => undefined)
            }
          >
            <option value="STUDENT">学生</option>
            <option value="TEACHER">教师</option>
            <option value="ADMIN">机构管理员</option>
          </select>
        )}
      </td>
      <td data-label="状态">
        <span
          className={`ac-status${inactive ? '' : locked ? ' ac-status--locked' : ' ac-status--active'}`}
        >
          {inactive ? '已停用' : locked ? '临时锁定' : '正常'}
        </span>
        {locked ? (
          <small className="ac-lock-hint">
            密码错误锁定至{' '}
            {new Date(member.loginLockedUntil!).toLocaleTimeString('zh-CN', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </small>
        ) : null}
      </td>
      <td data-label="操作">
        {isPlatform ? (
          <span className="ac-muted ac-protected">平台账号 · 受保护</span>
        ) : (
          <div className="ac-member-actions">
            <button
              className={`ac-text-button${nextStatus === 'INACTIVE' ? ' ac-text-button--danger' : ''}`}
              type="button"
              disabled={busy}
              onClick={changeLoginStatus}
            >
              {memberLoginActionLabel(member.status)}
            </button>
            {locked && member.role === 'STUDENT' && member.accountUsername !== null ? (
              <button
                type="button"
                className="ac-text-button ac-text-button--unlock"
                disabled={busy}
                onClick={unlock}
              >
                解除锁定
              </button>
            ) : null}
          </div>
        )}
      </td>
    </tr>
  );
}

function AdminEditor({
  title,
  busy,
  onClose,
  children,
}: {
  readonly title: string;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="ac-editor"
      aria-label={title}
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        const controls = event.currentTarget.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [href], [tabindex="0"]',
        );
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (first === undefined || last === undefined) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
    >
      <header>
        <div>
          <p className="ac-muted">棋院与用户管理</p>
          <h2>{title}</h2>
        </div>
        <button
          className="ac-close"
          aria-label="关闭弹窗"
          type="button"
          disabled={busy}
          onClick={onClose}
        >
          ×
        </button>
      </header>
      {children}
    </dialog>
  );
}
